import type { MailboxInfo } from "owa-client-types";
import { getIndexerValueForMailboxInfo } from "owa-client-types";
import { getItem, setItem, removeItem } from "owa-local-storage";
import type { LocalStorageKeys } from "owa-local-storage";
import type { getLoginHintClaimFunc, setLoginHintClaimFunc, deleteLoginHintClaimFunc, } from "./authCallbacks";

/**
 * Creates a login hint claim cache.
 *
 * This factory function provides the shared logic for caching login_hint claims
 * in both memory and local storage. Both OWA and Monarch use the same
 * account-scoped key: `login_hint_claim_${indexer}` (or `login_hint_claim` if no indexer).
 *
 * @returns Object with get/set/delete functions matching auth callback signatures
 */
export function createLoginHintCache(): {
    getLoginHintClaim: getLoginHintClaimFunc;
    setLoginHintClaim: setLoginHintClaimFunc;
    deleteLoginHintClaim: deleteLoginHintClaimFunc;
} {
    // In-memory cache of login_hint claims, keyed by mailboxInfo indexer value.
    // This provides fast lookups without hitting local storage on every token request.
    const loginHintClaimCache = new Map<string, string>();
    
    function getStorageKey(indexer: string): string {
        return `login_hint_claim_${indexer}`;
    }
    
    /**
     * Returns the cached login_hint claim for the given account.
     *
     * Lookup priority:
     * 1. In-memory cache (keyed by mailboxInfo indexer)
     * 2. Local storage (platform-specific key strategy)
     *
     * In Monarch (native host), the claim is populated after `lookupFirstPartyIdentity`
     * resolves an `IdentitySearchResult` that contains `loginHintClaim`.
     *
     * In OWA, the claim is populated from MSAL account's `idTokenClaims.login_hint`.
     *
     * @param mailboxInfo – The account to look up.
     * @returns The login_hint claim string, or undefined if not cached.
     */
    const getLoginHintClaim: getLoginHintClaimFunc = function (mailboxInfo: MailboxInfo): string | undefined {
        const key = getIndexerValueForMailboxInfo(mailboxInfo);
        
        // 1. In-memory cache
        const inMemory = loginHintClaimCache.get(key);
        if (inMemory) {
            return inMemory;
        }
        
        // 2. Local storage
        const storageKey = getStorageKey(key);
        const stored = getItem(self, storageKey as LocalStorageKeys);
        if (stored) {
            // Backfill memory cache for fast subsequent lookups
            loginHintClaimCache.set(key, stored);
            return stored;
        }
        
        return undefined;
    };
    
    /**
     * Caches the login_hint claim for the given account in both
     * in-memory cache and browser local storage.
     *
     * @param mailboxInfo – The account to store the claim for.
     * @param loginHintClaim – The login_hint claim value from the identity result.
     */
    const setLoginHintClaim: setLoginHintClaimFunc = function (mailboxInfo: MailboxInfo, loginHintClaim: string): void {
        const key = getIndexerValueForMailboxInfo(mailboxInfo);
        
        // In-memory cache
        loginHintClaimCache.set(key, loginHintClaim);
        
        // Local storage
        const storageKey = getStorageKey(key);
        setItem(self, storageKey as LocalStorageKeys, loginHintClaim);
    };
    
    /**
     * Deletes the cached login_hint claim for the given account from both
     * in-memory cache and browser local storage.
     *
     * @param mailboxInfo – The account to delete the claim for.
     */
    const deleteLoginHintClaim: deleteLoginHintClaimFunc = function (mailboxInfo: MailboxInfo): void {
        const key = getIndexerValueForMailboxInfo(mailboxInfo);
        
        // In-memory cache
        loginHintClaimCache.delete(key);
        
        // Local storage
        const storageKey = getStorageKey(key);
        removeItem(self, storageKey as LocalStorageKeys);
    };
    
    return {
        getLoginHintClaim,
        setLoginHintClaim,
        deleteLoginHintClaim,
    };
}
